import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { useWavesurfer } from "@wavesurfer/react";
//@ts-ignore
import Spectrogram from "wavesurfer.js/dist/plugins/spectrogram.esm.js";
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { ReactComponent as Pause } from "../assets/icons/pause.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import { Tab } from "@headlessui/react";
import { constants } from "../utils/ranges";
import { useAuth } from "../utils/auth";

import { ReactComponent as Download } from "../assets/icons/Download.svg";
import InfoCircle from "../assets/images/info-circle.png";
import Success from "../assets/images/pivc-success.png";

import CarePlixLogo from "../assets/images/CarePlixLogo.png";
import coverImageAll from "../assets/images/coverImageAll.png";
import BodyVitalReportImage from "../assets/images/BodyVitalReportImage.png";
import Heart from "../assets/images/heart.png";
import OxygenSaturation from "../assets/images/OxygenSaturation.png";
import HeartRate from "../assets/images/HeartRate.png";
import Metabolic from "../assets/images/metabolic.png";
import Stress from "../assets/images/stress.png";
import RespirationRate from "../assets/images/RespirationRate.png";
import HeartRateMax from "../assets/images/HeartRateMax.png";
import PNN50 from "../assets/images/PNN50.png";
import CardiacOutput from "../assets/images/CardiacOutput.png";
import MeanArterialPressure from "../assets/images/MeanArterialPressure.png";
import HeartUtilization from "../assets/images/HeartUtilization.png";
import HeartRateReserve from "../assets/images/HeartRateReserve.png";
import TargetHeartRateRange from "../assets/images/TargetHeartRateRange.png";
import VO2Max from "../assets/images/VO2Max.png";
import TotalBodyWater from "../assets/images/TotalBodyWater.png";
import TotalBodyFat from "../assets/images/TotalBodyFat.png";
import BloodVolume from "../assets/images/BloodVolume.png";
import BodyWater from "../assets/images/BodyWater.png";
import BloodPressure from "../assets/images/BloodPressure.png";
import Physiological from "../assets/images/Physiological.png";
import BodyWeight from "../assets/images/BodyWeight.png";
import stress from "../assets/images/stress.png";

import Personal from "../assets/images/Personal.png";
import MedicalHistory from "../assets/images/medicalHistory.png";
import RacentSymptoms from "../assets/images/RacentSymptoms.png";
import MedicationHospitalization from "../assets/images/MedicationHospitalization.png";
import FamilyHistory from "../assets/images/FamilyHistory.png";
import Pregency from "../assets/images/Pregency.png";
import GeneralHealth from "../assets/images/generalHealth.png";

import {
  Font,
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
  PDFDownloadLink,
  Svg,
  Path,
} from "@react-pdf/renderer";

import CardiovascularObservationResult from "./CardiovascularObservationResult";
import DermaResult from "./DermaResult";
import VisionResult from "./VisionResult";
import PIVCResult from "./PIVCResult";
// import AssessmentResult from "./AssessmentResult";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});

const ranges = {
  DryEyes: [
    {
      point: 0,
      color: "#DD3923",
      category: "High Risk",
      description:
        "A score in this range indicates a significantly high risk of dry eyes. Individuals may require immediate attention to improve eye hydration and comfort. Consistent management strategies, such as hydration, environmental modifications, and professional consultation, are highly recommended.",
    },
    {
      point: 25,
      color: "#F18233",
      category: "Moderate Risk",
      description:
        "This range suggests an increased likelihood of dry eyes, particularly in certain environments or activities. Preventive measures like maintaining hydration, following proper eye care routines, and reducing strain can help manage the risk effectively.",
    },
    {
      point: 50,
      color: "#FAC02B",
      category: "Mild Risk",
      description:
        "Individuals in this range have a low risk of dry eyes but may occasionally experience mild discomfort under specific conditions. Simple lifestyle adjustments and regular eye care practices can help maintain optimal ocular hydration.",
    },
    {
      point: 75,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates well-hydrated and healthy eyes with no significant risk of dryness. Maintaining good eye care habits and a balanced lifestyle can support long-term eye health.",
    },
    {
      point: 100,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates well-hydrated and healthy eyes with no significant risk of dryness. Maintaining good eye care habits and a balanced lifestyle can support long-term eye health.",
    },
  ],
  BlinkIndex: [
    {
      point: 0,
      color: "#DD3923",
      category: "High Risk",
      description:
        "A score in this range suggests a significantly high risk of dry eyes, often due to excessive blinking caused by tear film instability. Individuals may need to take corrective measures such as optimizing screen habits, improving hydration, and consulting an eye specialist.",
    },
    {
      point: 25,
      color: "#F18233",
      category: "Moderate Risk",
      description:
        "Individuals in this range show an increased blink rate, indicating a moderate risk of dry eyes. Environmental factors like prolonged screen time or air-conditioned surroundings could be contributing to this deviation. Adopting simple eye care practices can help restore balance.",
    },
    {
      point: 50,
      color: "#FAC02B",
      category: "Mild Risk",
      description:
        "A score in this range suggests a mostly stable blink rate with slight variations. The risk of dry eyes is low, but occasional factors like fatigue or temporary strain may cause mild fluctuations. Maintaining regular blinking habits and hydration can help sustain eye comfort.",
    },
    {
      point: 75,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates a healthy and stable blink rate, signifying no significant risk of dry eyes. Maintaining good screen hygiene, staying hydrated, and following natural blinking patterns will help preserve optimal eye health.",
    },
    {
      point: 100,
      color: "#64BD40",
      category: "No Risk",
      description:
        "A score in this range indicates a healthy and stable blink rate, signifying no significant risk of dry eyes. Maintaining good screen hygiene, staying hydrated, and following natural blinking patterns will help preserve optimal eye health.",
    },
  ],
  BlinkInterval: [
    {
      point: 0,
      color: "#A20303",
      category: "Very High",
      description:
        "Extremely frequent blinking, often indicating significant tear film instability and severe dry eye risk. Immediate attention to eye hydration and care is recommended.",
    },
    {
      point: 1,
      color: "#DD3923",
      category: "High",
      description:
        "Faster than normal blinking, suggesting a high likelihood of dry eyes. Could be due to environmental factors, excessive screen use, or underlying eye conditions.",
    },
    {
      point: 2,
      color: "#FAC02B",
      category: "Slightly Increased",
      description:
        "Moderately frequent blinking, potentially signaling early signs of dryness or strain. Maintaining good eye care practices can help restore balance.",
    },
    {
      point: 3,
      color: "#64BD40",
      category: "Normal",
      description:
        "Ideal blink interval, indicating a stable tear film and well-hydrated eyes. No significant risk of dry eyes, but maintaining regular eye care is beneficial.",
    },
    {
      point: 4,
      color: "#FAC02B",
      category: "Slightly Reduced",
      description:
        "Less frequent blinking than normal, which may suggest minor tear film irregularities. Awareness of blinking habits and hydration can help maintain eye comfort",
    },
    {
      point: 5,
      color: "#DD3923",
      category: "Low",
      description:
        "Extended blink intervals could indicate reduced tear production or prolonged focus, leading to potential discomfort over time. Conscious blinking and hydration may be helpful.",
    },
    {
      point: 6,
      color: "#A20303",
      category: "Very Low",
      description:
        "Significantly infrequent blinking, often linked to prolonged screen exposure or reduced tear secretion. Higher risk of eye strain and dryness, requiring proactive eye care.",
    },
    {
      point: 7,
      color: "#A20303",
      category: "Very Low",
      description:
        "Significantly infrequent blinking, often linked to prolonged screen exposure or reduced tear secretion. Higher risk of eye strain and dryness, requiring proactive eye care.",
    },
  ],
  OSDI: [
    {
      point: 0,
      color: "#64BD40",
      category: "Normal",
      description:
        "No significant symptoms of ocular surface disease. Individuals in this range typically experience stable tear film and good ocular comfort, with no noticeable visual disturbances or dryness-related discomfort in daily activities.",
    },
    {
      point: 13,
      color: "#FAC02B",
      category: "Mild",
      description:
        "Occasional or mild symptoms of ocular surface discomfort, such as intermittent dryness or slight irritation, particularly in challenging environments like air-conditioned spaces or prolonged screen use. Symptoms may be manageable with basic eye care practices.",
    },
    {
      point: 23,
      color: "#F18233",
      category: "Moderate",
      description:
        "More frequent or noticeable symptoms impacting daily activities, including increased dryness, sensitivity, and potential visual disturbances. Individuals in this range may require interventions such as artificial tears, blinking exercises, or environmental modifications.",
    },
    {
      point: 33,
      color: "#A20303",
      category: "Severe",
      description:
        "Persistent and significant ocular discomfort, with symptoms potentially affecting vision and quality of life. Individuals in this category may struggle with prolonged screen use, reading, or exposure to wind and air conditioning. Medical consultation and targeted treatments are often necessary.",
    },
    {
      point: 100,
      color: "#A20303",
      category: "Severe",
      description:
        "Persistent and significant ocular discomfort, with symptoms potentially affecting vision and quality of life. Individuals in this category may struggle with prolonged screen use, reading, or exposure to wind and air conditioning. Medical consultation and targeted treatments are often necessary.",
    },
  ],
};

const PDFReport = ({
  name = "",
  age = 0,
  gender = "",
  height = 0,
  weight = 0,
  result = {
    timestamp: new Date(),
    posture: "resting",
    bp_sys: 0,
    bp_dia: 0,
    resp_rate: 0,
    oxy_sat_prcnt: 0,
    heart_rate: 0,
    bmi: 0,
    sdnn: 0,
    rmssd: 0,
    pNN50_per: 0,
    cardiac_out: 0.0,
    map: 0,
    heart_utilized: 0,
    HRMax: 0,
    HRR: 0,
    THRR: 0,
    vo2max: 0.0,
    bloodvolume: 0.0,
    tbw: 0.0,
    tbwp: 0.0,
    bodyfat: 0.0,
    weight: 0.0,
    overallScore: 0,
    metabolicControlScore: 0,
    cardiacScore: 0,
    autonomicStressScore: 0,

    tbf: 0.0,
    emm: 0.0,
    fitnessLevel: "",

    hba1c: 0,

    vision: {} ?? {},
    dryEyeScore: 0,
    blinkIndex: 0,
    blinkIntervals: { avg: 0, max: 0, min: 0 },
    blinkCount: 0,
    leftEyeImage: "",
    rightEyeImage: "",

    isMatched: true,

    pulmo: {} ?? {},
    derma: {} ?? {},
    pivc: {} ?? {},
    dgmer: {} ?? {},
  },
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      backgroundColor: "#f7f7f7",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottom: 1,
      borderBottomColor: "#D3D3D3",
    },
    contentContainer: { flex: 1, paddingVertical: 12, paddingHorizontal: 24 },
    rowContainer: { flexDirection: "row", justifyContent: "space-between" },
    largeText: { fontFamily: "Poppins", fontSize: 15, fontWeight: "bold" },
    normalText: { fontFamily: "Poppins", fontSize: 12, fontWeight: "semibold" },
    smallText: { fontFamily: "Poppins", fontSize: 10 },
    footer: {
      height: 30,
      backgroundColor: "#192852",
      width: "100%",
    },
    coverPage: {
      backgroundColor: "#C3E6FF",
      height: "100%",
      paddingTop: 40,
      alignItems: "center",
    },

    coverLogo: {
      width: 220,
      height: 90,
      objectFit: "contain",
      alignSelf: "flex-start",
      marginLeft: 30,
    },
    coverTitleArea: {
      marginTop: 30,
      alignItems: "center",
    },

    reportSubtitle: {
      fontSize: 40,
      fontWeight: "bold",
      color: "#0078D4",
      fontFamily: "Poppins",
      textAlign: "center",
    },

    reportTitle: {
      fontSize: 48,
      fontFamily: "Poppins",
      textAlign: "center",
      marginTop: 4,
    },

    coverImageContainer: {
      width: "100%",
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      marginTop: 20,
    },

    coverImage: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
    headerText: {
      fontFamily: "Poppins",
      fontSize: 10,
      color: "#666",
    },
    circleContainer: {
      width: 130,
      height: 130,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#E9F3FF",
      borderRadius: 25,
      marginRight: 15,
      paddingVertical: 5,
      paddingHorizontal: 5,
    },

    titleText: {
      fontFamily: "Poppins",
      fontSize: 13,
      fontWeight: "600",
      color: "#1A1A1A",
      textAlign: "center",
      marginBottom: 8,
    },

    statusBox: {
      width: 80,
      height: 50,
      backgroundColor: "#FFFFFF",
      borderRadius: 30,
      justifyContent: "center",
      alignItems: "center",
      shadowColor: "#000",
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    scoreText: {
      fontFamily: "Poppins",
      fontSize: 10,
      fontWeight: "400",
      color: "#5A5A5A",
      textAlign: "center",
    },
    tableContainer: {
      marginTop: 10,
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeaderRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 5,
      paddingHorizontal: 12,
    },
    tableHeaderCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
      flex: 1,
    },
    tableRow: {
      flexDirection: "row",
      backgroundColor: "#fff",
      borderBottom: "1px solid #eee",
      paddingVertical: 8,
      paddingHorizontal: 12,
      alignItems: "center",
    },
    tableCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      color: "#333",
      flex: 1,
    },
    adjustedMuscleMassRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 4,
      paddingHorizontal: 12,
      borderRadius: 10,
    },
    adjustedCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
    },
    disclaimerContainer: {
      marginTop: 30,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#F4F7F8",
      border: "1px solid #eee",
    },
    disclaimerText: {
      fontFamily: "Poppins",
      fontSize: 9,
      fontWeight: "normal",
      color: "#666",
      textAlign: "justify",
    },
    bodyVitalsContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "#2E5894",
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 8,
      width: "100%",
    },
    textContainer: {
      flex: 1,
    },
    bodyVitalstitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#FFFFFF",
      marginBottom: 4,
    },
    highlight: {
      color: "#4BA3F2",
    },
    bodyVitalSubtitle: {
      fontSize: 10,
      color: "#DADADA",
    },
    bodyVitalsImage: {
      width: 70,
      height: 40,
      objectFit: "contain",
    },
    stressBox: {
      marginTop: 15,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#f7f7f7",
      border: "1px solid #eee",
    },
    aiLeft: {
      width: "60%",
    },
    aiTitle: {
      fontSize: 14,
      fontWeight: 700,
      marginBottom: 10,
      marginTop: 10,
    },
    sectionHeading: {
      fontSize: 12,
      color: "#555",
      marginBottom: 8,
    },
    badgeRow: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    badge: {
      backgroundColor: "#E8F7FF",
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 8,
      flexDirection: "row",
      gap: 6,
    },
    badgeLabel: {
      color: "#0A6D9F",
      fontSize: 11,
      fontWeight: 700,
    },
    badgeValue: {
      color: "#0A6D9F",
      fontSize: 11,
    },
    badgeGray: {
      backgroundColor: "#EBEBEB",
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 8,
    },
    badgeGrayLabel: {
      fontSize: 10,
      color: "#555",
    },
  });

  const Range = ({ ranges, selectedIndex, containerStyle }) => {
    selectedIndex -= Number(ranges.length - 1 === selectedIndex);

    return (
      <View
        style={[
          { width: "42.5%", alignSelf: "center", flexDirection: "row" },
          containerStyle,
        ]}
      >
        {ranges.slice(0, -1).map((rangeItem, index, list) => {
          return (
            <View
              key={`range-${index}`}
              style={{ flex: 1, position: "relative" }}
            >
              {index === selectedIndex && (
                <View style={{ position: "absolute", top: -10, left: 25 }}>
                  <Svg width={12} height={10} viewBox="0 0 10 6">
                    <Path
                      d="M0 0 L5 6 L10 0 Z"
                      fill={ranges[selectedIndex].color}
                    />
                  </Svg>
                </View>
              )}
              <View
                style={{
                  height: 5,
                  backgroundColor:
                    index === selectedIndex
                      ? ranges[selectedIndex].color
                      : "#c0c0c069",
                  borderTopLeftRadius: index > 0 ? 0 : 20,
                  borderBottomLeftRadius: index > 0 ? 0 : 20,
                  borderTopRightRadius: index < list.length - 1 ? 0 : 20,
                  borderBottomRightRadius: index < list.length - 1 ? 0 : 20,
                }}
              />
              <Text style={{ fontSize: 8, color: "#707070" }}>
                {rangeItem.point}
              </Text>
            </View>
          );
        })}
        <Text
          style={{
            fontFamily: "Poppins",
            fontSize: 8,
            lineHeight: 1.5,
            color: "#6E7A80",
            maxLines: 1,
            textOverflow: "ellipsis",
            marginTop: 4,
            marginRight: 2,
          }}
        >
          {ranges[ranges.length - 1].point}
        </Text>
      </View>
    );
  };

  const questionnaireData = [
    {
      title: "Personal & Lifestyle",
      icon: Personal,
      rows: [
        { q: "Do you smoke or use any tobacco products?", a: result.dgmer.q1 },
        { q: "Do you consume alcohol?", a: result.dgmer.q2 },
        {
          q: "Have you used recreational drugs in the last 12 months?",
          a: result.dgmer.q3,
        },
      ],
    },
    {
      title: "Medical History",
      icon: MedicalHistory,
      rows: [
        {
          q: "Have you ever been diagnosed with high blood pressure, diabetes, or high cholesterol?",
          a: result.dgmer.q4,
        },
        {
          q: "Have you ever been diagnosed with heart disease, stroke, or any heart-related condition?",
          a: result.dgmer.q5,
        },
        {
          q: "Have you ever been diagnosed with asthma, COPD, or chronic lung disease?",
          a: result.dgmer.q6,
        },
        {
          q: "Have you ever been diagnosed with kidney, liver, or thyroid disorders?",
          a: result.dgmer.q7,
        },
      ],
    },
    {
      title: "Recent Symptoms (last 3 months)",
      icon: RacentSymptoms,
      rows: [
        {
          q: "Have you experienced chest pain, breathlessness, or palpitations?",
          a: result.dgmer.q8,
        },
        {
          q: "Have you experienced sudden weight loss, fatigue, or fever in the last 3 months?",
          a: result.dgmer.q9,
        },
      ],
    },
    {
      title: "Medication & Hospitalization",
      icon: MedicationHospitalization,
      rows: [
        {
          q: "Are you currently taking any long-term medication?",
          a: result.dgmer.q10,
        },
        {
          q: "Have you been hospitalized or undergone surgery in the last 5 years?",
          a: result.dgmer.q11,
        },
      ],
    },
    {
      title: "Family History",
      icon: FamilyHistory,
      rows: [
        {
          q: "Family history of heart disease, stroke, diabetes, or cancer?",
          a: result.dgmer.q12,
        },
      ],
    },
    {
      title: "Women-specific to Pregnancy",
      icon: Pregency,
      rows: [
        {
          q: "Are you currently pregnant or undergoing any fertility treatment?",
          a: result.dgmer.q13,
        },
      ],
    },
    {
      title: "General Health Declaration",
      icon: GeneralHealth,
      rows: [
        {
          q: "Do you have any other medical conditions not listed above?",
          a: result.dgmer.q14,
        },
      ],
    },
  ];

  const Questionnaire = ({ title, icon = "", rows = [] }) => (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: 20,
          marginBottom: 5,
        }}
      >
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            backgroundColor: "#EAF5FF",
            border: "1px solid #A7CDF3",
            justifyContent: "center",
            alignItems: "center",
            marginRight: 8,
          }}
        >
          <Image src={icon} style={{ width: 10, height: 10 }} />
        </View>
        <Text style={[styles.smallText, { fontWeight: "bold" }]}>{title}</Text>
      </View>

      <View style={styles.tableContainer}>
        <View style={styles.tableHeaderRow}>
          <Text
            style={[styles.tableHeaderCell, { flex: 3.5, fontWeight: "bold" }]}
          >
            Question
          </Text>
          <Text
            style={[styles.tableHeaderCell, { flex: 0.5, fontWeight: "bold" }]}
          >
            Answer
          </Text>
        </View>

        {rows.map((item, idx) => (
          <View style={styles.tableRow} key={idx}>
            <Text style={[styles.tableCell, styles.smallText, { flex: 3.5 }]}>
              {item.q}
            </Text>
            <Text style={[styles.tableCell, styles.smallText, { flex: 0.5 }]}>
              {item.a}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const getStatus = (score) => {
    if (score <= 33) {
      return { label: "At Risk", color: "#C81616" };
    } else if (score <= 66) {
      return { label: "Moderate", color: "#FFC444" };
    } else {
      return { label: "Good", color: "#22C55E" };
    }
  };

  const severityPdfColors = {
    none: {
      backgroundColor: "#E6FFEB",
      borderColor: "#A3E6AF",
      textColor: "#22B459",
    },
    normal: {
      backgroundColor: "#E6FFEB",
      borderColor: "#A3E6AF",
      textColor: "#22B459",
    },
    mild: {
      backgroundColor: "#FEF9C3",
      borderColor: "#E8E3AD",
      textColor: "rgb(202,138,4)",
    },
    moderate: {
      backgroundColor: "rgb(255,247,237)",
      borderColor: "#fbead5",
      textColor: "rgb(234,88,12)",
    },
    frm: {
      backgroundColor: "#E6FFEB",
      borderColor: "#A3E6AF",
      textColor: "#22B459",
    },
    good: {
      backgroundColor: "#FEF9C3",
      borderColor: "#E8E3AD",
      textColor: "#854D0E",
    },
  };

  return (
    <Document
      title="CarePlix Report"
      subject="Report generated from CarePlix Vitals Scan"
      author="CarePlix"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.coverPage}>
          <Image style={styles.coverLogo} src={CarePlixLogo} />

          <View style={styles.coverTitleArea}>
            <Text style={styles.reportSubtitle}>UnityHealth</Text>
            <Text style={styles.reportTitle}>Assessment Report</Text>
          </View>

          <View style={styles.coverImageContainer}>
            <Image style={styles.coverImage} src={coverImageAll} />
          </View>
        </View>
      </Page>

      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <View>
            <Text
              style={{ color: "#00ABFF", fontWeight: "bold", fontSize: 18 }}
            >
              UnityHealth
            </Text>
            <Text style={{ fontWeight: "normal", fontSize: 20 }}>
              Assessment Report
            </Text>
          </View>
          <Text style={styles.headerText}>
            Date & Time:{" "}
            {new Date(result.timestamp).toLocaleDateString("en-GB")}
            {", "}
            {new Date(result.timestamp).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </Text>
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.rowContainer}>
            <View style={{ flex: 1.5, paddingRight: 40 }}>
              {[
                { label: "Name", value: name },
                { label: "Age", value: `${age} years` },
                { label: "Gender", value: gender },
                { label: "Height", value: `${height} cms` },
                { label: "Weight", value: `${weight} kgs` },
              ].map(({ label, value }) => (
                <Text
                  key={label.toLowerCase().replaceAll(" ", "-")}
                  style={[
                    styles.normalText,
                    {
                      fontWeight: "normal",
                      marginTop: label === "Name" ? 0 : 8,
                      borderBottom: 1,
                      borderBottomColor: "#E7E7E7",
                    },
                  ]}
                >
                  <Text style={{ fontWeight: "semibold", minWidth: 60 }}>
                    {label} :
                  </Text>
                  <Text
                    style={{
                      textTransform: label === "Name" ? "capitalize" : "none",
                    }}
                  >
                    {` ${value}`}
                  </Text>
                </Text>
              ))}
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              {[
                {
                  title: "Overall Health Score",
                  score: `${result.overallScore}%`,
                  status: result.overallStatus,
                },
              ].map((item) => (
                <View style={styles.circleContainer} key={item.title}>
                  <Text style={styles.titleText}>{item.title}</Text>
                  <View style={styles.statusBox}>
                    <Text style={styles.scoreText}>{item.score}</Text>
                    <Text style={styles.subtext}>{item.status}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>

          <Text
            style={{
              fontWeight: "semibold",
              fontSize: 12,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            Health Scores
          </Text>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2.5 }]}>
                Parameter
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Status</Text>
            </View>

            {[
              {
                icon: Metabolic,
                label: "Metabolic Control Score",
                value: Math.round(result.metabolicControlScore),
                status: getStatus(result.metabolicControlScore),
              },
              {
                icon: HeartRate,
                label: "Cardiac Score",
                value: Math.round(result.cardiacScore),
                status: getStatus(result.cardiacScore),
              },
              {
                icon: Stress,
                label: "Autonomic Stress Score",
                value: Math.round(result.autonomicStressScore),
                status: getStatus(result.autonomicStressScore),
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <View
                  style={[
                    styles.tableCell,
                    {
                      flex: 2.5,
                      flexDirection: "row",
                    },
                  ]}
                >
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 6,
                      backgroundColor: "#EAF5FF",
                      border: "1px solid #A7CDF3",
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: 8,
                    }}
                  >
                    <Image
                      src={item.icon}
                      style={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  </View>{" "}
                  <Text>{item.label}</Text>
                </View>

                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.value}
                </Text>
                <View
                  style={[
                    styles.smallText,

                    styles.tableCell,
                    {
                      alignItems: "center",
                      fontFamily: "Poppins",
                      fontSize: 10,
                      flex: 1,
                    },
                  ]}
                >
                  <Text style={{ color: item.status?.color || "#000" }}>
                    {item.status.label}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 2, marginBottom: 8, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgement of a
              health care professional. This scan is intended to improve you
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue.
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Key Body <Text style={styles.highlight}>Vitals</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Monitoring essential health metrics for optimal wellness
              </Text>
            </View>

            <Image src={BodyVitalReportImage} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Key Body Vitals
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Blood Pressure",
                value: `${result.bp_sys}/${result.bp_dia} mmHg`,
                status: (() => {
                  const sysStatus = constants[
                    "Blood Pressure"
                  ].ranges[0].reduce((p, c) =>
                    result.bp_sys >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const diaStatus = constants[
                    "Blood Pressure"
                  ].ranges[1].reduce((p, c) =>
                    result.bp_dia >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const sysIndex = constants[
                    "Blood Pressure"
                  ].ranges[0].findIndex((r) => r.label === sysStatus.label);
                  const diaIndex = constants[
                    "Blood Pressure"
                  ].ranges[1].findIndex((r) => r.label === diaStatus.label);

                  return sysIndex > diaIndex ? sysStatus : diaStatus;
                })(),
                range: "90–120/60–80 mmHg",
              },

              {
                label: "Heart Rate",
                value: `${result.heart_rate} BPM`,
                status: constants["Heart Rate"].ranges["resting"]?.reduce?.(
                  (p, c) => (result.heart_rate >= c.point ? c : p)
                ) || { label: "Unknown", color: "#000" },
                range: "60 - 100 BPM",
              },
              {
                label: "Respiration Rate",
                value: `${result.resp_rate} breaths/min`,
                status: constants["Respiration Rate"].ranges.reduce((p, c) =>
                  result.resp_rate >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "12 - 20/min",
              },
              {
                label: "Oxygen Saturation",
                value: `${result.oxy_sat_prcnt} %`,
                status: constants["Oxygen Saturation"].ranges.reduce((p, c) =>
                  result.oxy_sat_prcnt >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "95 - 100 %",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 8,
                        color: item.status?.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status?.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          {[
            {
              icon: BloodPressure,
              title: "Blood Pressure",
              desc: "Blood pressure is a measure of the force that the heart uses to pump blood around the body. Blood pressure is measured in millimeters of mercury (mmHg) and is given as 2 figures:\n\u2022 Systolic pressure - the pressure when your heart pushes blood out\n\u2022 Diastolic pressure - the pressure when your heart rests between beats",
            },
            {
              icon: HeartRate,
              title: "Heart Rate",
              desc: "The heart rate is the number of times heart beats in a minute (bp 4). A normal resting heart rate for adults ranges from 60 to 100 beats per minute. A person's resting heart rate is the heart rate during physical activity and exercise. A normal resting heart rate for adults ranges from 60 to 100 bpm.",
            },
            {
              icon: RespirationRate,
              title: "Respiration Rate",
              desc: "The respiration rate is the number of breaths taken in a minute. The rate is usually measured when a person is at rest and simply involves counting the number of breaths for one minute by counting how many times the chest rises.",
            },
            {
              icon: OxygenSaturation,
              title: "Oxygen Saturation",
              desc: "Oxygen saturation (also known as SpO2) is the measurement used to assess the amount of oxygen being carried by the blood and delivered to body's tissues.",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Heart <Text style={styles.highlight}>Health</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Understanding key heart health metrics.
              </Text>
            </View>

            <Image src={Heart} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Heart Health
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "SDNN",
                value: `${parseFloat(result.sdnn).toFixed(2)} ms`,
                range: "30 - 95 ms",
                status:
                  result.sdnn !== "--"
                    ? constants["SDNN"].ranges.reduce((p, c) =>
                        result.sdnn >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "RMSSD",
                value: `${parseFloat(result.rmssd).toFixed(2)} ms`,
                range: "20 - 89 ms",
                status:
                  result.rmssd !== "--"
                    ? constants["RMSSD"].ranges.reduce((p, c) =>
                        result.rmssd >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "PNN50",
                value: `${parseFloat(result.pNN50_per).toFixed(2)} %`,
                range: "5 - 40 %",
                status:
                  result.pNN50_per !== "--"
                    ? constants["PNN50"].ranges.reduce((p, c) =>
                        result.pNN50_per >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Cardiac Output",
                value: `${parseFloat(result.cardiac_out).toFixed(2)} L/min`,
                range: "4 - 7 L/min",
                status:
                  result.cardiac_out !== "--"
                    ? constants["Cardiac Output"].ranges.reduce((p, c) =>
                        result.cardiac_out >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Mean Arterial Pressure",
                value: `${result.map} mmHg`,
                range: "70 - 100 mmHg",
                status:
                  result.map !== "--"
                    ? constants["Mean Arterial Pressure"].ranges.reduce(
                        (p, c) => (result.map >= c.point ? c : p)
                      )
                    : undefined,
              },
              {
                label: "Heart Utilized",
                value: `${result.heart_utilized} %`,
                range: "41 - 70 %",
                status:
                  result.heart_utilized !== "--"
                    ? constants["Heart Utilized"].ranges.reduce((p, c) =>
                        result.heart_utilized >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Heart Rate Max",
                value: `${result.HRMax} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Heart Rate Reserve",
                value: `${result.HRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Target Heart Rate Range",
                value: `${result.THRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "VO2 Max",
                value: `${parseFloat(result.vo2max).toFixed(2)} mL/kg/min`,
                range: `42.5 mL/kg/min (Male)\n33 mL/kg/min (Female)`,
                status: { label: "", color: "" },
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2.5, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={[styles.tableCell, { flex: 1.5 }]}>
                  <Text>{item.value}</Text>
                  {item.status.label && (
                    <Text
                      style={{
                        fontFamily: "Poppins",
                        fontSize: 9,
                        fontWeight: "bold",
                        color: item.status.color,
                        marginTop: 2,
                      }}
                    >
                      {item.status.label}
                    </Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1.5, fontSize: item.label === "VO2 Max" ? 9 : 11 },
                  ]}
                >
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.contentContainer} break>
          {[
            {
              icon: HeartRate,
              title: "SDNN",
              desc: "The standard deviation of all of the NN intervals  (the time intervals between each heartbeat). Higher  numbers usually indicate that your body is coping  better with stress.",
            },
            {
              icon: HeartRate,
              title: "RMSSD",
              desc: "The root mean square of successive intervals be tween normal heartbeats (RMSSD) and is another  measure of heart rate variability ( HRV). RMSSD is  obtained by first calculating each successive time  intervals between heartbeats in msec.",
            },
            {
              icon: PNN50,
              title: "PNN50",
              desc: "PNN50 is a measure of the number of short term fluc tuations in the heart rate and is considered to reflect  changes in (HRV)  driven by the sympathetic branch  of the autonomic nervous system, that prepares the  body for stress and physical activity. PNN50 lower  than 3% is indicative of high risk.",
            },
            {
              icon: CardiacOutput,

              title: "Cardiac Output",
              desc: "The amount of blood  the heart pumps through the  circulatory system in a minute. Cardiac output is  generally 4-8 L/min in an rest.",
            },
            {
              icon: MeanArterialPressure,
              title: "Mean Arterial Pressure",
              desc: "MAP is the average arterial pressure throughout one  heart cycle. MAP is an important measurement that  accounts for flow, resistance, and pressure with in your arteries. It allows doctors to evaluate how  well blood flows through your body and whether it's  reaching all your major organs.",
            },
            {
              icon: HeartUtilization,
              title: "Heart Utilization",
              desc: "Heart utilization is the percentage of overall heart us age to its maximum peak heart rate. Generally < 50%  utilization at rest and 51-85% utilization at moderate  intensity is considered as normal.",
            },
            {
              icon: HeartRateMax,
              title: "Heart Rate Max",
              desc: "Maximum heart rate is the highest number of beats your heat can pump per  minute when it's under high stress (physical or otherwise). Knowing your max  HR can help you track your fitness progress and determine your target heart  rate for exercise intensity.",
            },
            {
              icon: HeartRateReserve,
              title: "Heart Rate Reserve",
              desc: "Heart rate reserve is the difference between your maximum (peak) heart rate  and your resting heart rate. The more fit you are, the lower your resting heart  rate will be. When you have a low resting heart rate, your heart rate reserve is  high.",
            },
            {
              icon: TargetHeartRateRange,
              title: "Target Heart Rate Range",
              desc: " To achieve a certain level of exertion, there is a minimum number of heartbeats  required within a specific timeframe.",
            },
            {
              icon: VO2Max,
              title: "VO2Max",
              desc: "VO2 Max is the maximum rate of oxygen consumption measured during incre mental exercise. Maximal oxygen consumption reflects your overall cardiores piratory fitness, and is an excellent way to assess your fitness level.\n A good V02 max for a man between ages 30 and 39 is 41 to 44.9 For women of the same age, a good V02 max is between 31.5 to 35.6",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* physiological */}

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                <Text style={styles.highlight}>Physiological</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={Physiological} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Physiological
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "BMI",
                value: `${result.bmi} kg/m²`,
                status: constants["BMI"].ranges.reduce((p, c) =>
                  result.bmi > c.point ? c : p
                ),
                range: "18.5-24.9 kg/m²",
              },
              {
                label: "Blood Volume",
                value: `${result?.bloodvolume}/ml`,
                range: "NA",
              },
              {
                label: "Total Body Water",
                value: `${result?.tbw}/Liters`,
                range: "NA",
              },
              {
                label: "Body Water %",
                value: `${result?.tbwp}/%`,
                range: "NA",
              },
              {
                label: "Total Body Fat",
                value: `${result?.bodyfat}%`,
                range: "NA",
              },
              {
                label: "Body Weight",
                value: `${result?.weight}/kg`,
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  {item.status && (
                    <Text
                      style={[
                        styles.smallText,
                        {
                          fontFamily: "Poppins",
                          fontSize: 9,
                          color: item.status.color || "#000",
                          fontWeight: "bold",
                          marginTop: 20,
                        },
                      ]}
                    >
                      {item.status.label || ""}
                    </Text>
                  )}
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            {[
              {
                icon: HeartRate,
                title: "BMI",
                desc: "Body Mass Index (BMI) is a measurement based on your weight and height. It is more of an indicator than a direct measurement of your total body fat.",
              },
              {
                icon: TotalBodyWater,
                title: "Total Body Water",
                desc: "Total Body Water is the total amount of water in the human body. This includes fluids both inside and outside of cells.",
              },
              {
                icon: TotalBodyFat,
                title: "Total Body Fat",
                desc: "Body fat percentage is an indicator of your body composition and indicates the amount of fat you have in your body.",
              },
              {
                icon: BloodVolume,
                title: "Blood Volume",
                desc: "Blood volume refers to the total amount of fluid circulating within the arteries, capillaries, veins, venules, and chambers of the heart at any time.",
              },
              {
                icon: BodyWater,
                title: "Body Water %",
                desc: "Most of the human body is water, with an average of roughly 60%. The amount of water in the body changes slightly with age, sex, and hydration levels.",
              },
              {
                icon: BodyWeight,
                title: "Body Weight",
                desc: "Body weight refers to the total mass of a person's body, which is composed of various tissues, such as bones, muscles, organs, and body fat.",
              },
            ].map(({ title, desc, icon }, index) => (
              <View
                key={index}
                style={{
                  width: "47%",
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 12,
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
                wrap={false}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundColor: "#EAF5FF",
                    border: "1px solid #A7CDF3",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  <Image src={icon} style={{ width: 26, height: 26 }} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: 3,
                    }}
                  >
                    {title}
                  </Text>
                  <Text
                    style={{
                      fontSize: 9,
                      lineHeight: 1.3,
                      color: "#475569",
                      textAlign: "justify",
                    }}
                  >
                    {desc}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Stress */}

        <View style={styles.contentContainer} break>
          <View style={[styles.bodyVitalsContainer]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Blood <Text style={styles.highlight}>Biomarkers</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Blood Biomarker
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "HbA1c",
                value: result.hba1c,
                status:
                  constants["HbA1c"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.hba1c >= r.point) || {},
                range: "< 5.7%",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={[styles.stressBox, { flexDirection: "row" }]}>
            <View
              style={{
                width: 30,
                height: 30,
                justifyContent: "center",
                alignItems: "center",
                marginRight: 8,
              }}
            >
              <Image src={BloodVolume} style={{ width: 26, height: 26 }} />
            </View>
            <View>
              <Text style={[styles.largeText, { fontSize: 10 }]}>
                Blood Biomarker
              </Text>

              <Text style={styles.smallText}>
                Your HbA1c Level is{" "}
                {constants["HbA1c"].ranges
                  .slice()
                  .reverse()
                  .find((r) => result.hba1c >= r.point)?.label || "Low"}
                .Maintain a healthy lifestyle to support long-term well-being.
              </Text>
            </View>
          </View>
          {/* stress */}
          <View style={[styles.bodyVitalsContainer, { marginTop: 60 }]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Stress <Text style={styles.highlight}>Level</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Stress Level
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Stress Index",
                value: result.stress,
                status:
                  constants["Stress Level"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.stress >= r.point) || {},
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={styles.stressBox}>
            <Text style={[styles.largeText, { fontSize: 10 }]}>
              Your Stress Level is{" "}
              {constants["Stress Level"].ranges
                .slice()
                .reverse()
                .find((r) => result.stress >= r.point)?.label || "Low"}
            </Text>
            <Text style={styles.smallText}>
              The body's reaction to a challenge or demand. There are 5 levels
              of stress.
            </Text>
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 10, marginBottom: 5, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgment of a
              health care professional. This scan is intended to improve your
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue
            </Text>
          </View>
        </View>

        {/* Vision */}
        {result?.vision && Object.keys(result.vision).length > 0 && (
          <View style={styles.contentContainer} break>
            <View style={[styles.bodyVitalsContainer, { height: 60 }]}>
              <View style={styles.textContainer}>
                <Text style={styles.bodyVitalstitle}>
                  <Text style={styles.highlight}>Ophthalmology</Text>
                </Text>
                <Text style={styles.bodyVitalSubtitle}>
                  Specialized care for eye diseases, vision correction, and
                  overall ocular health.
                </Text>
              </View>
            </View>
            <View style={{ marginTop: 30 }}>
              {[
                {
                  label: "Dry Eye Risk Indicator",
                  value: result.dryEyeScore,
                  unit: "%",
                  ranges: ranges.DryEyes,
                  caregoryVisible: true,
                },
                {
                  label: "Blink Index (BI)",
                  value: result.blinkIndex,
                  unit: "%",
                  ranges: ranges.BlinkIndex,
                },
                {
                  label: "Blink Interval",
                  value: result.blinkIntervals?.avg ?? NaN,
                  unit: "s",
                  ranges: ranges.BlinkInterval,
                },
                {
                  label: "Ocular Surface Disease Index (OSDI)",
                  value: result.vision?.osdi?.toFixed?.(2) ?? NaN,
                  unit: "",
                  ranges: ranges.OSDI,
                },
              ].map((item, index) => {
                const value = parseFloat(item.value);
                const selectedRangeIndex = isNaN(value)
                  ? -1
                  : item.ranges.reduce(
                      (p, c, ci) => (value > c.point ? ci : p),
                      0
                    );
                //@ts-ignore
                const { color, category, description } = isNaN(value)
                  ? {}
                  : item.ranges[selectedRangeIndex];
                return (
                  <View
                    key={item.label.toLowerCase().replaceAll(" ", "-")}
                    style={{
                      borderWidth: 1,
                      borderColor: "#C2DAE8",
                      borderRadius: 10,
                      marginBottom: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        gap: 10,
                        marginBottom: 5,
                        padding: 10,
                      }}
                    >
                      <View style={{ flex: 1, gap: 0 }}>
                        <Text
                          style={{
                            fontFamily: "Poppins",
                            fontSize: 12,
                            lineHeight: 1.2,
                            fontWeight: "bold",
                            color: "#0D212C",
                            marginBottom: 6,
                          }}
                        >
                          {item.label}
                        </Text>
                        <View style={{ display: "flex", flexDirection: "row" }}>
                          <View
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-end",
                              marginRight: 10,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 14,
                                fontFamily: "Poppins",
                                fontWeight: "bold",
                                color: color,
                              }}
                            >
                              {isNaN(value) ? "--" : value.toFixed(1)}
                            </Text>
                            <Text
                              style={{
                                fontSize: 8,
                                color: "#000",
                                marginBottom: 5,
                                marginLeft: 3,
                              }}
                            >
                              {item.unit}
                            </Text>
                          </View>
                          <View
                            style={{
                              backgroundColor:
                                typeof color === "string"
                                  ? `${color}20`
                                  : undefined,
                              borderRadius: 5,
                              height: 20,
                              paddingHorizontal: 10,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 9,
                                color: color,
                                fontFamily: "Poppins",
                                fontWeight: "medium",
                              }}
                            >
                              {category}
                            </Text>
                          </View>
                        </View>
                      </View>
                      <View
                        style={{
                          alignSelf: "center",
                          gap: 10,
                          width: "47.5%",
                        }}
                      >
                        <Range
                          containerStyle={{
                            width: "100%",
                            alignSelf: "stretch",
                          }}
                          ranges={item.ranges}
                          selectedIndex={selectedRangeIndex}
                        />
                      </View>
                    </View>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        backgroundColor: "rgba(14,110,165,0.1)",
                        borderBottomLeftRadius: 10,
                        borderBottomRightRadius: 10,
                        paddingHorizontal: 10,
                        paddingVertical: 6,
                      }}
                    >
                      <Image
                        src={InfoCircle}
                        style={{ width: 15, height: 15, marginRight: 5 }}
                      />
                      <Text
                        style={{
                          fontSize: 8,
                          fontFamily: "Poppins",
                          color: "rgba(0,0,0,0.7)",
                          width: "97%",
                          lineHeight: 1.1,
                        }}
                      >
                        {description}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <View
                style={{
                  width: "65%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <View>
                  <Image
                    src={result.rightEyeImage}
                    style={{ width: 160, height: 80 }}
                  />

                  <View
                    style={{
                      backgroundColor: "#161616",
                      padding: 5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={{
                        textAlign: "center",
                        fontSize: 10,
                        color: "#fff",
                        fontFamily: "Poppins",
                      }}
                    >
                      Right Eye
                    </Text>
                  </View>
                </View>
                <View style={{ marginLeft: 10 }}>
                  <Image
                    src={result.leftEyeImage}
                    style={{ width: 160, height: 80 }}
                  />

                  <View
                    style={{
                      backgroundColor: "#161616",
                      padding: 5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={{
                        textAlign: "center",
                        fontSize: 10,
                        color: "#fff",
                        fontFamily: "Poppins",
                      }}
                    >
                      Left Eye
                    </Text>
                  </View>
                </View>
              </View>
              <View style={{ width: "37%" }}>
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: "Poppins",
                    fontWeight: "semibold",
                    marginBottom: 5,
                  }}
                >
                  Blink Intervals
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#CFE2ED",
                    borderRadius: 10,
                  }}
                >
                  {[
                    {
                      label: "Blinks Count",
                      value: result.blinkCount ?? "--",
                    },
                    {
                      label: "Avg. Interval",
                      value: `${
                        result.blinkIntervals?.avg.toFixed(1) ?? "--"
                      } sec`,
                    },
                    {
                      label: "Max Interval",
                      value: `${
                        result.blinkIntervals?.max.toFixed(1) ?? "--"
                      } sec`,
                    },
                    {
                      label: "Min Interval",
                      value: `${
                        result.blinkIntervals?.min.toFixed(1) ?? "--"
                      } sec`,
                    },
                  ].map((d, idx) => (
                    <View
                      key={idx}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        backgroundColor:
                          idx % 2 === 0 ? "#F4F5F9" : "transparent",
                        borderTopRightRadius: idx === 0 ? 10 : 0,
                        borderTopLeftRadius: idx === 0 ? 10 : 0,
                        paddingHorizontal: 10,
                        paddingVertical: 3,
                      }}
                    >
                      <Text style={{ fontSize: 10, fontFamily: "Poppins" }}>
                        {d.label}:
                      </Text>
                      <Text style={{ fontSize: 10, fontFamily: "Poppins" }}>
                        {d.value}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>
        )}
        {/* derma */}
        <View style={styles.contentContainer} break>
          {result.derma && (
            <>
              <View style={[styles.bodyVitalsContainer, { height: 60 }]}>
                <View style={styles.textContainer}>
                  <Text style={styles.bodyVitalstitle}>
                    <Text style={styles.highlight}>Dermatology</Text>
                  </Text>
                  <Text style={styles.bodyVitalSubtitle}>
                    Dermatology is dedicated to skin health and overall skin
                    wellness.
                  </Text>
                </View>
              </View>
              <View style={styles.rowContainer}>
                <View style={styles.aiLeft}>
                  <Text style={styles.aiTitle}>
                    AI analysis for your “face”
                  </Text>

                  {/* DETECTED CONDITIONS */}

                  <Text style={styles.sectionHeading}>Detected Conditions</Text>
                  <View
                    style={{
                      backgroundColor: "#E3F2FF",
                      borderRadius: 10,
                      padding: 5,
                    }}
                  >
                    {result?.derma?.detected_conditions.length > 0 ? (
                      <>
                        <View style={styles.badgeRow}>
                          <View style={styles.badge}>
                            <Text style={styles.normalText}>
                              {result?.derma?.detected_conditions?.[0]
                                ?.condition
                                ? result.derma.detected_conditions[0].condition
                                    .charAt(0)
                                    .toUpperCase() +
                                  result.derma.detected_conditions[0].condition.slice(
                                    1
                                  )
                                : ""}{" "}
                            </Text>
                          </View>
                          <View style={styles.badge}>
                            <Text style={styles.smallText}>
                              {result?.derma?.detected_conditions?.[0]?.severity
                                ? result.derma?.detected_conditions[0].severity
                                    .charAt(0)
                                    .toUpperCase() +
                                  result.derma?.detected_conditions[0].severity.slice(
                                    1
                                  )
                                : ""}
                            </Text>
                          </View>
                          <View style={styles.badgeGray}>
                            <Text style={styles.smallText}>
                              {result.derma?.detected_conditions[0].confidence *
                                100}
                              % confidence
                            </Text>
                          </View>
                        </View>
                        <Text style={styles.smallText}>
                          {result.derma?.detected_conditions[0].description}
                        </Text>
                      </>
                    ) : (
                      <Text style={styles.smallText}>
                        No significant skin conditions detected.{" "}
                      </Text>
                    )}
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    marginTop: 10,
                  }}
                >
                  {[
                    {
                      title: "Overall Skin Health",

                      status: result.derma.overall_skin_health,
                    },
                  ].map((item) => {
                    const severityLower = String(item.status).toLowerCase();

                    const colors = severityPdfColors[severityLower] || {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                      textColor: "#374151",
                    };
                    return (
                      <View style={styles.circleContainer} key={item.title}>
                        <Text style={styles.titleText}>{item.title}</Text>
                        <View style={styles.statusBox}>
                          <Text
                            style={[
                              styles.normalText,
                              { color: colors.textColor },
                            ]}
                          >
                            {item.status
                              ? item.status.charAt(0).toUpperCase() +
                                item.status.slice(1)
                              : ""}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
              <Text
                style={{
                  fontWeight: "semibold",
                  fontSize: 12,
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                Comprehensive Facial Analysis
              </Text>
              <View style={styles.tableContainer}>
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                    Parameter
                  </Text>
                  <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                    Condition
                  </Text>
                  <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                    Score
                  </Text>
                  <Text style={[styles.tableHeaderCell, { flex: 2.5 }]}>
                    Description
                  </Text>
                </View>
                {Object.entries(result?.derma.facial_analysis ?? {}).map(
                  ([key, val], index) => {
                    if (key === "pores_detailed_zones") return null;

                    const label = key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase());

                    const severityOrLevel =
                      val?.severity || val?.level || val?.visibility || "N/A";

                    const severityLower = String(severityOrLevel).toLowerCase();

                    const severityColors = severityPdfColors[severityLower] || {
                      backgroundColor: "#F3F4F6",
                      borderColor: "#D1D5DB",
                      textColor: "#374151",
                    };

                    const confidence = val?.confidence
                      ? `${Math.round(val.confidence * 100)}%`
                      : "—";

                    const description = val?.description || "—";

                    return (
                      <View style={styles.tableRow} key={index}>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.smallText,
                            { flex: 2 },
                          ]}
                        >
                          {label}
                        </Text>

                        <Text
                          style={{
                            flex: 1,
                            fontSize: 10,
                            color: severityColors.textColor,
                          }}
                        >
                          {severityOrLevel}
                        </Text>

                        <Text
                          style={[
                            styles.tableCell,
                            styles.smallText,
                            { flex: 1 },
                          ]}
                        >
                          {confidence}
                        </Text>

                        <Text
                          style={[
                            styles.tableCell,
                            styles.smallText,
                            { flex: 2.5 },
                          ]}
                        >
                          {description}
                        </Text>
                      </View>
                    );
                  }
                )}
              </View>
              <Text
                style={{
                  fontWeight: "semibold",
                  fontSize: 12,
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                Pore Analysis by Zone
              </Text>
              <View style={styles.tableContainer}>
                <View style={styles.tableHeaderRow}>
                  <Text style={[styles.tableHeaderCell, { flex: 2.5 }]}>
                    Parameter
                  </Text>
                  <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                    Condition
                  </Text>
                  <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                    Score
                  </Text>
                </View>
                {[
                  {
                    label: "Spots",
                    severity: result.derma.facial_analysis.spots.severity,
                    confidence: result.derma.facial_analysis.spots.confidence,
                  },
                  {
                    label: "Wrinkles",
                    severity: result.derma.facial_analysis.wrinkles.severity,
                    confidence:
                      result.derma.facial_analysis.wrinkles.confidence,
                  },
                  {
                    label: "Dark Circles",
                    severity:
                      result.derma.facial_analysis.dark_circles.severity,
                    confidence:
                      result.derma.facial_analysis.dark_circles.confidence,
                  },
                ].map((item, index) => {
                  const severityLower = String(item.severity).toLowerCase();

                  const colors = severityPdfColors[severityLower] || {
                    backgroundColor: "#F3F4F6",
                    borderColor: "#D1D5DB",
                    textColor: "#374151",
                  };

                  return (
                    <View style={styles.tableRow} key={index}>
                      {/* Label */}
                      <Text
                        style={[
                          styles.tableCell,
                          { flex: 2.5 },
                          styles.smallText,
                        ]}
                      >
                        {item.label}
                      </Text>

                      <View style={[styles.tableCell]}>
                        <Text
                          style={{
                            flex: 1.5,
                            fontSize: 10,
                            fontFamily: "Poppins",
                            color: colors.textColor,
                            textTransform: "capitalize",
                          }}
                        >
                          {item.severity}
                        </Text>
                      </View>

                      <Text
                        style={[
                          styles.tableCell,
                          styles.smallText,
                          { flex: 1.5 },
                        ]}
                      >
                        {item.confidence
                          ? `${Math.round(item.confidence * 100)}%`
                          : "—"}
                      </Text>
                    </View>
                  );
                })}
              </View>
              <Text
                style={{
                  fontWeight: "semibold",
                  fontSize: 12,
                  marginTop: 20,
                  marginBottom: 10,
                }}
              >
                Care Recommendations
              </Text>
              {Object.entries(result?.derma.recommendations ?? {}).map(
                ([title, content], i) => (
                  <View
                    key={i}
                    style={{
                      color: "#000000",
                      fontSize: 10,
                      borderRadius: 10,
                      paddingHorizontal: 10,
                      paddingVertical: 6,
                      marginBottom: 6,
                      flexDirection: "row",
                    }}
                  >
                    <Text style={{ marginRight: 4 }}>{i + 1}.</Text>
                    <Text>{content}</Text>
                  </View>
                )
              )}
            </>
          )}
          {/* pulmo */}
          {result.pulmo && (
            <>
              <View
                style={[
                  styles.bodyVitalsContainer,
                  { height: 60, marginTop: 20, marginBottom: 5 },
                ]}
              >
                <View style={styles.textContainer}>
                  <Text style={styles.bodyVitalstitle}>
                    <Text style={styles.highlight}>Pulmonary Health </Text>
                  </Text>
                  <Text style={styles.bodyVitalSubtitle}>
                    Evaluates respiratory strength, airflow efficiency, and lung
                    performance.
                  </Text>
                </View>
              </View>
              <View style={styles.rowContainer}>
                <View style={styles.aiLeft}>
                  <Text
                    style={{
                      marginTop: 0,
                      fontSize: 12,
                      textAlign: "center",
                      marginBottom: 10,
                      marginTop: 10,
                    }}
                  >
                    {"Assessment indicates "}
                    <Text
                      style={{
                        fontWeight: "bold",
                        color: result.pulmo.isOADScoreGood
                          ? "#59D341"
                          : "#FF2B2B",
                      }}
                    >
                      {result.pulmo.isOADScoreGood ? "no presence" : "presence"}
                    </Text>
                    {" of obstructive airway disease (OAD)"}
                  </Text>
                  <Text
                    style={{
                      backgroundColor: "#EDF5FF",
                      padding: 5,
                      borderRadius: 5,
                      fontSize: 12,
                      margin: 5,
                    }}
                  >
                    Predicted FEV1/FVC:{result?.pulmo.fev1fvcRatio}
                  </Text>
                  <Text
                    style={{
                      backgroundColor: "#EDF5FF",
                      padding: 5,
                      borderRadius: 5,
                      fontSize: 12,
                      margin: 5,
                    }}
                  >
                    Pulse-Respiration Quotient (PRQ):{result?.pulmo.prq}
                  </Text>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "center",
                    marginTop: 10,
                  }}
                >
                  {[
                    {
                      title: "Pulmonary Health Score",
                      score: `${Math.floor(result.pulmo.pulmoScore)}%`,
                    },
                  ].map((item) => (
                    <View style={styles.circleContainer} key={item.title}>
                      <Text style={styles.titleText}>{item.title}</Text>
                      <View style={styles.statusBox}>
                        <Text style={styles.scoreText}>{item.score}</Text>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </>
          )}

          {/* pivc */}
          {result.pivc && (
            <>
              <View
                style={[
                  styles.bodyVitalsContainer,
                  { height: 60, marginTop: 20, marginBottom: 10 },
                ]}
              >
                <View style={styles.textContainer}>
                  <Text style={styles.bodyVitalstitle}>
                    <Text style={styles.highlight}>PIVC</Text>
                  </Text>
                </View>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 10,
                  marginTop: 10,
                }}
              >
                <View style={{ width: "45%" }}>
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: "bold",
                      marginBottom: 6,
                    }}
                  >
                    Face Match Status
                  </Text>

                  <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Image
                      src={Success}
                      style={{ width: 15, height: 15, marginRight: 5 }}
                    />
                    <Text style={{ fontSize: 12 }}>
                      {result.pivc?.is_matched ? "Yes" : "No"}
                    </Text>
                  </View>
                </View>

                <View style={{ width: "35%" }}>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                      Face Match Recognized
                    </Text>
                    <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                      {result.pivc?.match_confidence}%
                    </Text>
                  </View>

                  <View
                    style={{
                      marginTop: 6,
                      height: 8,
                      width: "100%",
                      borderRadius: 4,
                      backgroundColor: "#ffffff",
                      border: "1px solid #ccc",
                    }}
                  >
                    <View
                      style={{
                        height: "100%",
                        width: `${result.pivc?.match_confidence}%`,
                        backgroundColor: "#22C55E",
                        borderRadius: 4,
                      }}
                    />
                  </View>
                </View>
              </View>
            </>
          )}

          {/* Digi MER */}
          {result.dgmer.q1 && (
            <View style={styles.contentContainer} break>
              <View
                style={[
                  styles.bodyVitalsContainer,
                  { height: 60, marginTop: 20, marginBottom: 5 },
                ]}
              >
                <View style={styles.textContainer}>
                  <Text style={styles.bodyVitalstitle}>
                    <Text style={styles.highlight}>Digi MER</Text>
                  </Text>
                  <Text style={styles.bodyVitalSubtitle}>
                    Digital Health Questionnaire Report
                  </Text>
                </View>
              </View>
              {questionnaireData.map((block, idx) => (
                <Questionnaire
                  key={idx}
                  title={block.title}
                  icon={block.icon}
                  rows={block.rows}
                />
              ))}
            </View>
          )}
        </View>

        <View fixed style={styles.footer} />
      </Page>
    </Document>
  );
};

const ScoreData = ({ isGood, fev1fvcRatio, pulmoScore }) => {
  return (
    <>
      <div className="score-card px-5 py-5 w-full bg-[#1D1D1D] rounded-2xl relative z-50 mb-6">
        <div className="flex flex-row justify-between items-center mb-3">
          <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
            Pulmonary Health Score
          </h4>
        </div>

        <p className="text-xxs text-white/70 text-justify mt-1 mb-4">
          {" "}
          Indicates your overall lung function health based on cough analysis,
          breathing pattern, and voice sample combined with your FEV1/FVC ratio.
        </p>

        <div className="progress-col relative mt-14 mb-1">
          <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
            <div
              className="progress-bar"
              style={{
                width: `${Math.floor(pulmoScore)}%`,
              }}
            >
              <div className="progress-value">{Math.floor(pulmoScore)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="circle-blur lg:w-96 lg:h-96 md:w-96 md:h-96 w-80 h-80 mx-auto absolute left-0 right-0 top-20 blur-[90px]" />
      <h1 className="mt-0 text-2xl relative z-50 text-center mb-5">
        {"Assessment indicates "}
        <span
          className="font-bold"
          style={{ color: isGood ? "#59D341" : "#FF2B2B" }}
        >
          {isGood && "no "}presence
        </span>
        {" of obstructive airway disease (OAD)"}
      </h1>
      <div
        className={`mt-4 p-4 relative z-50 rounded-2xl ${
          isGood ? "bg-success/35" : "bg-error/25"
        } text-sm`}
      >
        <h2 className="font-semibold">
          {isGood && "No "}Obstructive Airway Disease (OAD) Risk Found:
        </h2>
        <p className="mt-1 font-light">
          {"Your assessment shows "}
          <span className="font-semibold">{isGood && "no "}risk</span>
          {/* {` of having ${
            fev1fvcRatio < 0.65 ? "COPD" : "Asthma/COPD"
          } at this time.`} */}
          {` of having Asthma/COPD at this time.`}
        </p>
      </div>
      <div className="mt-5 p-6 rounded-[20px] bg-[#1D1D1D]/70 relative z-50">
        <h2 className="font-light text-secondary">
          Predicted FEV1/FVC:{" "}
          <span className="font-semibold text-white">{fev1fvcRatio}</span>
        </h2>
        <h3 className="mt-3 text-xs font-semibold">What is FEV1/FVC Value?</h3>
        <p className="mt-2 text-xxs relative z-50">
          The FEV1/FVC ratio is a key lung function indicator that compares the
          amount of air you can forcefully exhale in one second (FEV1) to the
          total amount of air you can exhale after taking a deep breath (FVC).
          <br />
          It helps assess how well your lungs are working and is commonly used
          to detect airflow obstruction in conditions like Asthma or COPD.
          <br />
          <br />✅ Normal Range: Typically above 70-80% (varies by age)
          <br />
          <br />✅ Lower Values: May indicate obstructive lung disease
        </p>
        <div className="circle-blur w-32 h-32 mx-auto absolute right-0 bottom-0 blur-[50px]"></div>
      </div>
      <div className="mt-6 p-6 rounded-[20px] bg-[#364560] text-wgite relative z-50">
        <h3 className="text-sm font-semibold text-secondary">
          For Investigational Use Only :
        </h3>
        <p className="mt-2 text-xxs">
          Careplix Pulmo is not a medical device and should not be used as a
          substitute for clinical judgment by a health care professional.
          CarePlix Pulmo is intended to Improve your awareness of your lung
          obstruction health and not intended to diagnose, treat, mitigate or
          prevent any disease, symptom, disorder or abnormal physical condition.
          Contact your health-care provider Immediately if you suspect that you
          have a medical problem. Statements regarding clinical data have not
          been evaluated by the US FDA and are only intended for self-awareness
          and general wellness purposes only.
        </p>
      </div>
    </>
  );
};

const AudioSample = ({ title, audioURL, onPoorQuality }) => {
  const waveformContainerRef = useRef(null);
  const { isReady, isPlaying, wavesurfer } = useWavesurfer({
    container: waveformContainerRef,
    url: audioURL,
    height: 40,
    interact: false,
    waveColor: "#fff",
    progressColor: "#808080",
    cursorColor: "#fff",
    cursorWidth: 2,
    barGap: 1,
    barWidth: 2,
    barRadius: 999,
  });
  const [quality, setQuality] = useState("Good");

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.registerPlugin(
        Spectrogram.create({ height: 60, splitChannels: false })
      );
      wavesurfer.on("decode", (duration) => {
        const decodedData = wavesurfer.getDecodedData();
        if (decodedData) {
          const audioData = decodedData.getChannelData(0);
          const minValue = 0.025;
          const minSilenceDuration = 0.1;
          const mergeDuration = 0.2;
          const scale = duration / audioData.length;
          const silentRegions = [];
          let start = 0;
          let end = 0;
          let isSilent = false;
          for (let i = 0; i < audioData.length; i++) {
            if (audioData[i] < minValue) {
              if (!isSilent) {
                start = i;
                isSilent = true;
              }
            } else if (isSilent) {
              end = i;
              isSilent = false;
              if (scale * (end - start) > minSilenceDuration) {
                silentRegions.push({
                  start: scale * start,
                  end: scale * end,
                });
              }
            }
          }
          const mergedRegions = [];
          let lastRegion = null;
          for (let i = 0; i < silentRegions.length; i++) {
            if (
              lastRegion &&
              silentRegions[i].start - lastRegion.end < mergeDuration
            ) {
              lastRegion.end = silentRegions[i].end;
            } else {
              lastRegion = silentRegions[i];
              mergedRegions.push(lastRegion);
            }
          }
          const regions = [];
          let lastEnd = 0;
          for (let i = 0; i < mergedRegions.length; i++) {
            regions.push({
              start: lastEnd,
              end: mergedRegions[i].start,
            });
            lastEnd = mergedRegions[i].end;
          }
          if (title === "Cough") {
            if (regions.length >= 3 && regions.length <= 6) setQuality("Good");
            else {
              setQuality("Poor");
              onPoorQuality();
            }
          } else if (title === "Voice") {
            if (regions.length !== 1) {
              setQuality("Poor");
              onPoorQuality();
            } else setQuality("Good");
          }
        }
      });
    }
    return () => {
      wavesurfer?.unAll?.();
    };
  }, [title, wavesurfer, onPoorQuality]);

  return (
    <div className="mt-4 space-y-1 bg-white">
      <h2 className="text-primary font-medium">Recorded {title} Sample</h2>
      <div className="px-4 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D] text-secondary">
        <button
          className="h-10 w-10"
          type="button"
          disabled={!isReady}
          onClick={() => {
            wavesurfer?.playPause?.();
          }}
        >
          <span className="sr-only">Play/Pause</span>
          {isPlaying ? <Pause /> : <Play />}
        </button>
        <div
          className="grow border-l-2 border-secondary"
          ref={waveformContainerRef}
        />
      </div>
      {quality === "Poor" && (
        <p className="text-primary text-xxs">
          <span className="text-error">
            We detected that the audio sample might not be accurate.
          </span>
          <br />
          <span>
            The recording doesn't seem to have the expected characteristics.
          </span>
          &nbsp;
          {title === "Voice" ? (
            <span>
              Please ensure that you're using the correct pronunciation and
              holding the sound consistently.
            </span>
          ) : (
            title === "Cough" && (
              <span>
                The recording doesn't seem to have the expected characteristics.
                Please ensure you're coughing naturally and directly into the
                microphone.
              </span>
            )
          )}
        </p>
      )}
    </div>
  );
};

const AllCombinedResult = () => {
  const { state } = useLocation();
  const {
    userData: { fname, lname, dob, gender, height, weight },
  } = useAuth();
  const [audioQualityLevel, setAudioQualityLevel] = useState(3);
  const [eyeImageBase64, setEyeImageBase64] = useState({ left: "", right: "" });

  // const allergyResult = {
  //   data: {
  //     date: "2025-11-20T08:58:25.126Z",
  //     image_url:
  //       "https://files-carenowdoc-com-official-website-development.s3.amazonaws.com/careplix/AllergyAI/49a925f4-f8ef-4097-a9df-a1b605587670/f298fed3-7af3-4f59-8d39-843ac1575f19/face.jpeg",
  //     location: "Kolkata, India",
  //     facial_score: 44,
  //     environmental_score: 31,
  //     overall_risk_score: 39,
  //     risk_level: "Moderate",
  //     symptoms_detected: {
  //       eye_redness: false,
  //       eye_puffiness: true,
  //       nasal_inflammation: true,
  //       dark_circles: false,
  //       facial_fatigue: false,
  //     },
  //     environmental_data: {
  //       aqi: 162,
  //       pollen_count: "Moderate",
  //       pm2_5: 78.5,
  //       pm10: 80.3,
  //       humidity: 53,
  //       temperature: 27.9,
  //       weather_condition: "Mainly Clear",
  //       weather_summary:
  //         "The air quality is poor due to high levels of PM2.5 and PM10, which may trigger allergic symptoms.",
  //       location_display_name: "Kolkata, India",
  //     },
  //     facial_risk_level: "Moderate",
  //     environmental_risk_level: "Moderate",
  //     detailed_scores: {
  //       facial: {
  //         score: 4.4,
  //         risk_level: "Moderate",
  //       },
  //       environmental: {
  //         score: 3.1,
  //         risk_level: "Moderate",
  //       },
  //       combined: {
  //         score: 3.9,
  //         risk_level: "Moderate",
  //         contributors: {
  //           facial: {
  //             score: 4.4,
  //             risk_level: "Moderate",
  //           },
  //           environmental: {
  //             score: 3.1,
  //             risk_level: "Moderate",
  //           },
  //         },
  //       },
  //     },
  //     recommendation: {
  //       "Immediate relief measures for current symptoms":
  //         "Use saline nasal spray to clear nasal passages and reduce congestion. An over-the-counter antihistamine may help alleviate sneezing and itching. Apply a cool, damp cloth to the face to soothe irritation.",
  //       "Preventive actions based on environmental conditions":
  //         "Keep windows closed during high pollen days and use air conditioning with a clean air filter. Consider using a dehumidifier to control indoor humidity levels, especially in damp environments.",
  //       "When to seek medical attention":
  //         "Consult a healthcare provider if symptoms persist despite over-the-counter medication, impact your daily life, or if you experience difficulty breathing or severe sinus pressure.",
  //       "Lifestyle modifications to reduce exposure":
  //         "Shower and change clothing after spending time outdoors to remove pollen. Use protective coverings on pillows and mattresses to reduce exposure to dust mites. Keep indoor spaces clean and dry to minimize mold. Consider using allergy-proof bedding materials.",
  //     },
  //     image_analyis: {
  //       face_detected: true,
  //       reason_if_no_face: null,
  //       image_quality: "good",
  //       lighting_quality: "good",
  //       face_position: "good",
  //       visible_features: {
  //         eyes: false,
  //         nose: true,
  //         mouth: true,
  //       },
  //       overall_confidence: 80,
  //     },
  //     confidence_data: {
  //       face_detected: true,
  //       reason_if_no_face: null,
  //       image_quality: "good",
  //       lighting_quality: "good",
  //       face_position: "good",
  //       visible_features: {
  //         eyes: false,
  //         nose: true,
  //         mouth: true,
  //       },
  //       overall_confidence: 80,
  //     },
  //   },
  // };

  const PulmoTab = useMemo(() => {
    if (!(state?.selectedAssesments?.pulmo === true)) return null;
    return (
      <Tab.Panel>
        <div className="px-6 !mb-10 tab-screen">
          <ScoreData
            isGood={state?.pulmoResult.isOADScoreGood}
            fev1fvcRatio={state?.pulmoResult.fev1fvcRatio}
            pulmoScore={state?.pulmoResult.pulmoScore}
          />
        </div>
        <div className=" bg-white px-6 pt-3 pb-20">
          {audioQualityLevel < 3 && (
            <div className="mt-6 p-6 flex items-center space-x-4 rounded-[20px] bg-[#f8b62d]">
              <svg
                className="shrink-0 w-8"
                viewBox="0 0 122.88 111.54"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#000"
                  d="M2.35,84.42,45.28,10.2l.17-.27h0A23,23,0,0,1,52.5,2.69,17,17,0,0,1,61.57,0a16.7,16.7,0,0,1,9.11,2.69,22.79,22.79,0,0,1,7,7.26q.19.32.36.63l42.23,73.34.24.44h0a22.48,22.48,0,0,1,2.37,10.19,17.63,17.63,0,0,1-2.17,8.35,15.94,15.94,0,0,1-6.93,6.6c-.19.1-.39.18-.58.26a21.19,21.19,0,0,1-9.11,1.75v0H17.61c-.22,0-.44,0-.65,0a18.07,18.07,0,0,1-6.2-1.15A16.42,16.42,0,0,1,3,104.24a17.53,17.53,0,0,1-3-9.57,23,23,0,0,1,1.57-8.74,7.66,7.66,0,0,1,.77-1.51Z"
                />
                <path
                  fill="#f8b62d"
                  d="M9,88.75,52.12,14.16c5.24-8.25,13.54-8.46,18.87,0l42.43,73.69c3.39,6.81,1.71,16-9.33,15.77H17.61C10.35,103.8,5.67,97.43,9,88.75Z"
                />
                <path
                  fill="#000"
                  d="M57.57,83.78A5.53,5.53,0,0,1,61,82.2a5.6,5.6,0,0,1,2.4.36,5.7,5.7,0,0,1,2,1.3,5.56,5.56,0,0,1,1.54,5,6.23,6.23,0,0,1-.42,1.35,5.57,5.57,0,0,1-5.22,3.26,5.72,5.72,0,0,1-2.27-.53A5.51,5.51,0,0,1,56.28,90a5.18,5.18,0,0,1-.36-1.27,5.83,5.83,0,0,1-.06-1.31h0a6.53,6.53,0,0,1,.57-2,4.7,4.7,0,0,1,1.14-1.56Zm8.15-10.24c-.19,4.79-8.31,4.8-8.49,0-.82-8.21-2.92-29.34-2.86-37.05.07-2.38,2-3.79,4.56-4.33a12.83,12.83,0,0,1,5,0c2.61.56,4.65,2,4.65,4.44v.24L65.72,73.54Z"
                />
              </svg>
              <p className="text-xxs">
                This assessment result is generated based on your Face scan
                {audioQualityLevel === 2
                  ? ", Cough audio sample"
                  : audioQualityLevel === 1
                  ? ", Voice audio sample"
                  : ""}{" "}
                and questionnaire responses. The{" "}
                {audioQualityLevel === 2
                  ? "Voice"
                  : audioQualityLevel === 1
                  ? "Cough"
                  : "Voice & Cough"}{" "}
                sample recording did not meet the required characteristics and
                was excluded from the overall assessment computation.
              </p>
            </div>
          )}
          <div className="tab-screen grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-4 gap-3">
            <AudioSample
              title="Voice"
              audioURL={state?.pulmoResult.aVowelAudioB64Data ?? ""}
              onPoorQuality={() => setAudioQualityLevel((s) => s - 1)}
            />
            <AudioSample
              title="Cough"
              audioURL={state?.pulmoResult.coughAudioB64Data ?? ""}
              onPoorQuality={() => setAudioQualityLevel((s) => s - 2)}
            />

            <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
              <h3 className="mb-4 text-lg">Heart Rate</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={state?.pulmoResult.hrTimeData.data}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="chart-bar-gradient-hr"
                      x1="0%"
                      y1="50%"
                      x2="100%"
                      y2="50%"
                    >
                      <stop offset="25%" stopColor="#0EA573" />
                      <stop offset="75%" stopColor="#063BC4" />
                    </linearGradient>
                  </defs>
                  <XAxis
                    type="number"
                    dataKey="time"
                    allowDataOverflow={false}
                    domain={[0, 35]}
                    ticks={[5, 10, 15, 20, 25, 30]}
                    tickLine={false}
                    tickFormatter={(v) => `${v} sec`}
                    fontSize={10}
                    stroke="#fff"
                    strokeOpacity={0.75}
                    strokeWidth={1}
                  />
                  <YAxis
                    type="number"
                    dataKey="value"
                    domain={["dataMin - 8", "dataMax + 8"]}
                    tick={false}
                    tickMargin={0}
                    stroke="#fff"
                    strokeOpacity={0.75}
                    strokeWidth={1}
                    width={1}
                  />
                  <Line
                    type="monotone"
                    strokeWidth={6}
                    stroke="url(#chart-bar-gradient-hr)"
                    dataKey="value"
                    dot={false}
                  />
                  <ReferenceLine
                    x={10}
                    isFront={true}
                    stroke="#fff"
                    strokeOpacity={0.5}
                    strokeDasharray="3 3"
                  />
                  <ReferenceLine
                    x={25}
                    isFront={true}
                    stroke="#fff"
                    strokeOpacity={0.5}
                    strokeDasharray="3 3"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-1 flex flex-row justify-between gap-6">
                <div>
                  <h4 className="text-secondary text-sm font-semibold">
                    min HR:
                  </h4>
                  <p>
                    <span className="font-semibold">
                      {state?.pulmoResult.hrTimeData.data[
                        state?.pulmoResult.hrTimeData.min
                      ]?.value ?? "--"}
                    </span>
                    &nbsp;
                    <span className="text-xs">bpm</span>
                  </p>
                  <p className="text-xxs font-light">
                    ⏱️{" "}
                    {state?.pulmoResult.hrTimeData.data[
                      state?.pulmoResult.hrTimeData.min
                    ]?.time ?? "--"}{" "}
                    seconds
                  </p>
                </div>
                <div>
                  <h4 className="text-secondary text-sm font-semibold">
                    max HR:
                  </h4>
                  <p>
                    <span className="font-semibold">
                      {state?.pulmoResult.hrTimeData.data[
                        state?.pulmoResult.hrTimeData.max
                      ]?.value ?? "--"}
                    </span>
                    &nbsp;
                    <span className="text-xs">bpm</span>
                  </p>
                  <p className="text-xxs font-light">
                    ⏱️{" "}
                    {state?.pulmoResult.hrTimeData.data[
                      state?.pulmoResult.hrTimeData.max
                    ]?.time ?? "--"}{" "}
                    seconds
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
              <h3 className="mb-4 text-lg">Respiration Rate</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={state?.pulmoResult.rrTimeData.data}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="chart-bar-gradient-rr"
                      x1="0%"
                      y1="50%"
                      x2="100%"
                      y2="50%"
                    >
                      <stop offset="25%" stopColor="#063BC4" />
                      <stop offset="75%" stopColor="#0EA573" />
                    </linearGradient>
                  </defs>
                  <XAxis
                    type="number"
                    dataKey="time"
                    allowDataOverflow={false}
                    domain={[0, 35]}
                    ticks={[5, 10, 15, 20, 25, 30]}
                    tickLine={false}
                    tickFormatter={(v) => `${v} sec`}
                    fontSize={10}
                    stroke="#fff"
                    strokeOpacity={0.75}
                    strokeWidth={1}
                  />
                  <YAxis
                    type="number"
                    dataKey="value"
                    domain={["dataMin - 2", "dataMax + 2"]}
                    tick={false}
                    tickMargin={0}
                    stroke="#fff"
                    strokeOpacity={0.75}
                    strokeWidth={1}
                    width={1}
                  />
                  <Line
                    type="monotone"
                    strokeWidth={6}
                    stroke="url(#chart-bar-gradient-rr)"
                    dataKey="value"
                    dot={false}
                  />
                  <ReferenceLine
                    x={10}
                    isFront={true}
                    stroke="#fff"
                    strokeOpacity={0.5}
                    strokeDasharray="3 3"
                  />
                  <ReferenceLine
                    x={25}
                    isFront={true}
                    stroke="#fff"
                    strokeOpacity={0.5}
                    strokeDasharray="3 3"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-1 flex flex-row justify-between gap-6">
                <div>
                  <h4 className="text-secondary text-sm font-semibold">
                    min RR:
                  </h4>
                  <p>
                    <span className="font-semibold">
                      {state?.pulmoResult.rrTimeData.data[
                        state?.pulmoResult.rrTimeData.min
                      ]?.value ?? "--"}
                    </span>
                    &nbsp;
                    <span className="text-xs">breathes/min</span>
                  </p>
                  <p className="text-xxs font-light">
                    ⏱️{" "}
                    {state?.pulmoResult.rrTimeData.data[
                      state?.pulmoResult.rrTimeData.min
                    ]?.time ?? "--"}{" "}
                    seconds
                  </p>
                </div>
                <div>
                  <h4 className="text-secondary text-sm font-semibold">
                    max RR:
                  </h4>
                  <p>
                    <span className="font-semibold">
                      {state?.pulmoResult.rrTimeData.data[
                        state?.pulmoResult.rrTimeData.max
                      ]?.value ?? "--"}
                    </span>
                    &nbsp;
                    <span className="text-xs">breathes/min</span>
                  </p>
                  <p className="text-xxs font-light">
                    ⏱️{" "}
                    {state?.pulmoResult.rrTimeData.data[
                      state?.pulmoResult.rrTimeData.max
                    ]?.time ?? "--"}{" "}
                    seconds
                  </p>
                </div>
              </div>
            </div>

            <div className="col-span-full">
              <div className="mt-4 px-6 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D]">
                <h3 className="text-white font-semibold">
                  Pulse-Respiration Quotient (PRQ)
                </h3>
                <p className="text-secondary text-2xl font-semibold">
                  {state?.pulmoResult.prq || "--"}
                </p>
              </div>
              <Link
                to={-2}
                className="block mt-8 px-6 py-3 lg:w-1/2 md:w-1/2 w-full mx-auto rounded-full outline-none border border-primary/80 bg-white text-primary text-center text-sm font-light"
              >
                Back
              </Link>
            </div>
          </div>
        </div>
      </Tab.Panel>
    );
  }, [state?.selectedAssesments?.pulmo, state?.pulmoResult, audioQualityLevel]);

  const VisionTab = useMemo(() => {
    if (!(state?.selectedAssesments?.vision === true)) return null;
    return (
      <Tab.Panel>
        <VisionResult
          result={{
            ...(state?.visionResult ?? {}),
            scanResult: state?.scanResult ?? {},
          }}
          onEyeImage={(side, data) =>
            setEyeImageBase64((ei) => ({ ...ei, [side]: data }))
          }
        />
      </Tab.Panel>
    );
  }, [
    state?.selectedAssesments?.vision,
    state?.visionResult,
    state?.scanResult,
  ]);

  const DermaTab = useMemo(() => {
    if (state?.selectedAssesments?.derma === true) {
      if (state?.dermaResult === undefined) {
        return (
          <Tab.Panel>
            <div className="pt-2">
              <div className="circle-blur w-[70%] h-64 mx-auto absolute left-0 right-0 top-16 blur-[100px]"></div>
              <p className="text-center text-sm text-error pt-2 mt-10  mb-10">
                Derma analysis could not be generated .
              </p>
            </div>
          </Tab.Panel>
        );
      }

      return (
        <Tab.Panel>
          <DermaResult state={state?.dermaResult} />
        </Tab.Panel>
      );
    }

    return null;
  }, [state?.selectedAssesments?.derma, state?.dermaResult]);

  function imageDataToBase64(imageData) {
    const offscreen = new OffscreenCanvas(imageData.width, imageData.height);
    const ctx = offscreen.getContext("2d");
    ctx.putImageData(imageData, 0, 0);

    return offscreen.convertToBlob({ type: "image/png" }).then((blob) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    });
  }

  useEffect(() => {
    const left = state?.visionResult?.leftEyeImageData;
    const right = state?.visionResult?.rightEyeImageData;

    if (left?.data?.length > 0) {
      imageDataToBase64(left).then((base64) =>
        setEyeImageBase64((ei) => ({ ...ei, left: base64 }))
      );
    }

    if (right?.data?.length > 0) {
      imageDataToBase64(right).then((base64) =>
        setEyeImageBase64((ei) => ({ ...ei, right: base64 }))
      );
    }
  }, [state?.visionResult]);

  const { blinkCount, blinkIntervals } = useMemo(() => {
    if (!state?.visionResult?.eyeBlinkData?.length) {
      return { blinkCount: null, blinkIntervals: null };
    }

    let blinks = 0;
    let blinkIntervalList = [];
    let eyeTriggerStart = 0;
    let eyeTrigger = true;

    state.visionResult.eyeBlinkData.forEach(({ confidence, timestamp }) => {
      if (confidence > 0.4) eyeTrigger = true;
      else if (eyeTrigger) {
        const duration = Math.abs(timestamp - eyeTriggerStart) / 1000;
        if (duration >= 0.5) {
          blinkIntervalList.push(duration);
          blinks++;
        }
        eyeTriggerStart = timestamp;
        eyeTrigger = false;
      }
    });

    if (blinks === 0 || blinkIntervalList.length === 0) {
      return { blinkCount: null, blinkIntervals: null };
    }

    return {
      blinkCount: blinks,
      blinkIntervals: {
        avg:
          blinkIntervalList.reduce((s, v) => s + v, 0) /
          blinkIntervalList.length,
        max: Math.max(...blinkIntervalList),
        min: Math.min(...blinkIntervalList),
      },
    };
  }, [state?.visionResult]);

  const blinkIndex = useMemo(() => {
    if (typeof blinkCount !== "number") return null;

    return blinkCount > 8
      ? Math.min(100 - (blinkCount - 15) * 5, 100)
      : Math.max(100 - (9 - blinkCount) * 20, 0);
  }, [blinkCount]);

  const dryEyeScore = useMemo(() => {
    if (!state?.visionResult) return null;
    let score = 0;
    let weight = 0;
    if (typeof state?.visionResult?.osdi === "number") {
      score +=
        state?.visionResult.osdi < 50
          ? (100 - state?.visionResult.osdi * 2) * 4
          : 0;
      weight += 4;
    }
    if (Number.isFinite(blinkIndex)) {
      score += blinkIndex * 3;
      weight += 3;
    }
    if (
      typeof state?.scanResult?.metadata?.heart_scores?.stress_index ===
      "number"
    ) {
      score += 100 - state.scanResult.metadata.heart_scores.stress_index * 10;
      weight += 1;
    }
    return Math.min(Math.max(Math.round(score / weight), 0), 100);
  }, [blinkIndex, state]);

  const reportPDF = useMemo(() => {
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);

    return (
      <PDFReport
        name={`${fname} ${lname}`}
        age={age}
        gender={gender.slice(0, 1).toUpperCase() + gender.slice(1)}
        height={height}
        weight={weight}
        result={{
          timestamp: new Date(
            state?.scanResult?.entry_time?.replace?.(" ", "T") ?? 0
          ),
          bp_sys: state?.scanResult?.vitals.bp_sys,
          bp_dia: state?.scanResult?.vitals.bp_dia,
          resp_rate: state?.scanResult?.vitals.resp_rate,
          oxy_sat_prcnt: state?.scanResult?.vitals.oxy_sat_prcnt,
          heart_rate: state?.scanResult?.vitals.heart_rate,
          bmi: state?.scanResult?.metadata.physiological_scores.bmi,
          sdnn:
            state?.scanResult?.metadata.heart_scores.sdnn !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.sdnn)
              : "--",
          rmssd:
            state?.scanResult?.metadata.heart_scores.rmssd !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.rmssd)
              : "--",
          pNN50_per: state?.scanResult?.metadata.heart_scores.pNN50_per,
          cardiac_out: state?.scanResult?.metadata.cardiovascular.cardiac_out,
          map: state?.scanResult?.metadata.cardiovascular.map,
          heart_utilized:
            state?.scanResult?.metadata.heart_scores.heart_utilized,
          HRMax: state?.scanResult?.metadata.heart_scores.HRMax,
          HRR: state?.scanResult?.metadata.heart_scores.HRR,
          THRR: state?.scanResult?.metadata.heart_scores.THRR,
          vo2max: state?.scanResult?.metadata.physiological_scores.vo2max,
          bloodvolume:
            state?.scanResult?.metadata.physiological_scores.bloodvolume,
          tbw: state?.scanResult?.metadata.physiological_scores.tbw,
          tbwp: state?.scanResult?.metadata.physiological_scores.tbwp,
          bodyfat: state?.scanResult?.metadata.physiological_scores.bodyfat,
          weight: state?.scanResult?.metadata.physiological_scores.weight,
          stress: state?.scanResult?.metadata.heart_scores.stress_index,
          overallScore: state?.scanResult?.wellness_score,
          metabolicControlScore:
            state?.scanResult?.metadata?.glucose_info?.diabetes_control_score ??
            0,
          cardiacScore: state?.scanResult?.metadata?.overall_heart_score ?? 0,
          autonomicStressScore:
            state?.scanResult?.metadata?.mental_wellbeing_score ?? 0,
          hba1c: state?.scanResult?.metadata.glucose_info.hba1c,
          pulmo: state?.pulmoResult,
          dryEyeScore: dryEyeScore,
          vision: state?.visionResult,
          blinkIndex: blinkIndex,
          blinkIntervals: blinkIntervals,
          blinkCount: blinkCount,
          leftEyeImage: eyeImageBase64.left,
          rightEyeImage: eyeImageBase64.right,
          derma: state?.dermaResult?.analysis,
          pivc: state?.faceMatchResult,
          dgmer: state?.dgmerAnswers,
        }}
      />
    );
  }, [
    state,
    fname,
    lname,
    dob,
    gender,
    height,
    weight,
    blinkCount,
    blinkIndex,
    blinkIntervals,
    eyeImageBase64,
    dryEyeScore,
  ]);

  return (
    <section className="pt-2">
      <div className="relative z-50 flex items-center m-6">
        <Link
          to={-1}
          className="w-9 h-9 mr-6 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="back arrow" />
        </Link>
        <h2 className="text-xl font-medium">Assessment Result</h2>
      </div>
      <Tab.Group>
        <Tab.List className="mb-6 px-4 flex justify-start md:justify-center space-x-3 overflow-x-auto tab-link w-full">
          {[
            state?.selectedAssesments?.scan === true && "Vitals",
            state?.selectedAssesments?.faceMatch === true && "Face Match",
            state?.selectedAssesments?.pulmo === true && "Pulmonary",
            state?.selectedAssesments?.vision === true && "Vision",
            state?.selectedAssesments?.derma === true && "Derma",
            // state?.dermaResult === undefined &&
            // "Derma",
            // state?.selectedAssesments?.allergy === true && "Allergy",
          ]
            .filter((v) => typeof v === "string")
            .map((item) => (
              <Tab
                key={item}
                className={({ selected }) =>
                  `relative z-50 px-5 py-2 rounded-full whitespace-nowrap outline-none text-sm font-medium transition-colors duration-500 ${
                    selected
                      ? "bg-white text-black"
                      : "bg-[#2D2D2D] text-white/70"
                  }`
                }
              >
                {item}
              </Tab>
            ))}
        </Tab.List>

        <Tab.Panels>
          {state?.selectedAssesments?.scan === true && (
            <Tab.Panel>
              <CardiovascularObservationResult result={state?.scanResult} />
            </Tab.Panel>
          )}
          {state?.selectedAssesments?.faceMatch === true && (
            <Tab.Panel>
              <PIVCResult
                image={state?.faceMatchResult?.faceImage}
                isSuccess={state?.faceMatchResult?.is_matched}
                percentage={
                  parseFloat(state?.faceMatchResult?.match_confidence ?? 0) || 0
                }
              />
            </Tab.Panel>
          )}
          {PulmoTab}
          {VisionTab}
          {DermaTab}
          {/* <Tab.Panel>
            <AssessmentResult state={allergyResult} />
          </Tab.Panel> */}
        </Tab.Panels>
      </Tab.Group>
      <PDFDownloadLink
        className="flex items-center justify-between space-x-4 py-3 px-6 rounded-xl bg-black text-white text-sm font-medium"
        document={reportPDF}
        fileName="Unity-Health-Report"
      >
        {({ loading }) => (
          <>
            <span>
              {loading ? "Loading document..." : "Download PDF Report"}
            </span>
            <Download className="flex-shrink-0 h-6 w-6" />
          </>
        )}
      </PDFDownloadLink>
    </section>
  );
};

export default AllCombinedResult;
